import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { UiInput, UiInputView } from '@nx-frontend/shared/ui/input';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { addNoteToPortal, getPreparedPortalNoteMenuItem } from '@nx-frontend/shared/actions';
import {
  $menuState,
  getMaxIndexFx,
  deleteMenuItemEvent,
  getMenuSlugs,
  webnotesAddMenuItemEvent,
  setActiveItemIdEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $note } from '@nx-frontend/shared/store/note';
import { addNoteToPortalEvent } from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $portalSettings, setPortalExpandedItemInSidebarEvent } from '@nx-frontend/shared/store/portal';
import { $user } from '@nx-frontend/shared/store/user';
import { MenuType, AddPageToPortalPayload } from '@nx-frontend/shared/types';

export function AddPageToPortalPopup(params: { content: AddPageToPortalPayload }) {
  const { content } = params;
  const [noteVisibility, setNoteVisibilty] = useState(true);
  const [pageName, setPageName] = useState('');
  const { globalId, workspaceId } = content;
  const [menuState, portalSettings, user, note, closeModal, orgInfo, getMaxIndex, setPortalExpandedItemInSidebar] =
    useUnit([
      $menuState,
      $portalSettings,
      $user,
      $note,
      closeModalEvent,
      $orgInfo,
      getMaxIndexFx,
      setPortalExpandedItemInSidebarEvent,
    ]);
  const [moveNoteToPortal, deleteMenuItem, addMenuItem, setActiveItemId] = useEvent([
    addNoteToPortalEvent,
    deleteMenuItemEvent,
    webnotesAddMenuItemEvent,
    setActiveItemIdEvent,
  ]);
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations('webnotes');

  useEffect(() => {
    setPageName(note.title);
  }, [note, setPageName]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPageName(e.target.value);
    },
    [setPageName]
  );

  const handleVisibilityChange = useCallback(() => {
    setNoteVisibilty(!noteVisibility);
  }, [noteVisibility, setNoteVisibilty]);

  const handleAddPage = useCallback(async () => {
    const currentMaxIndex = await getMaxIndex({ menuType: MenuType.PORTAL_SIDEBAR });
    const existedSlugs = getMenuSlugs(menuState, [
      MenuType.PORTAL_TOPBAR,
      MenuType.PORTAL_SIDEBAR,
      MenuType.PORTAL_FOOTER,
    ]);
    const noteMenuItem = getPreparedPortalNoteMenuItem({
      noteId: globalId,
      index: currentMaxIndex ? currentMaxIndex + 1 : 0,
      pageName: pageName,
      pageVisibility: noteVisibility,
      existedSlugs: existedSlugs,
    });

    const isNotePublishedSuccesfully = await addNoteToPortal({
      userId: user.id,
      portalId: portalSettings.id,
      orgId: orgInfo.orgId,
      workspaceId: workspaceId,
      noteId: globalId,
      menuItem: noteMenuItem,
    });

    if (isNotePublishedSuccesfully) {
      moveNoteToPortal({
        workspaceId,
        globalId,
        showMakeInvisibleNotification: noteVisibility,
      });
      deleteMenuItem({
        menuType: MenuType.WEBNOTES_MENU,
        item: menuState['webnotes-menu'].entities[`note#${globalId}`],
      });
      addMenuItem({ menuType: MenuType.PORTAL_SIDEBAR, item: noteMenuItem });
      setActiveItemId({ menuType: MenuType.PORTAL_SIDEBAR, id: noteMenuItem.id });
      setPortalExpandedItemInSidebar({
        portalId: portalSettings.id,
        itemId: 'portal',
        expanded: true,
      });
      setPortalExpandedItemInSidebar({
        portalId: portalSettings.id,
        itemId: 'contentAndLinks',
        expanded: true,
      });
      setPortalExpandedItemInSidebar({
        portalId: portalSettings.id,
        itemId: MenuType.PORTAL_SIDEBAR,
        expanded: true,
      });
      closeModal();
      router.push(`/portal/${portalSettings.id}/visual/${noteMenuItem.url}`);
    } else {
      toast({ title: 'Failed connect page to portal, try again' });
    }
  }, [
    getMaxIndex,
    menuState,
    globalId,
    pageName,
    noteVisibility,
    user.id,
    portalSettings.id,
    orgInfo.orgId,
    workspaceId,
    moveNoteToPortal,
    deleteMenuItem,
    addMenuItem,
    setActiveItemId,
    setPortalExpandedItemInSidebar,
    closeModal,
    router,
    toast,
  ]);

  return (
    <div className="inline-flex	p-[0] flex-col items-start rounded gap-4">
      <span className="font-bold text-lg">{t('webnotes-connect_page_to_portal')}</span>
      <div className="flex flex-col gap-4">
        <div className="w-[356px] ">
          <div className="font-normal uppercase text-[10px] text-neutral-500 leading-[120%] mb-2">
            {t('webnotes-page_name')}
          </div>
          <UiInput
            id="page_name"
            type="text"
            label=""
            value={pageName}
            name="page_name"
            error={false}
            placeholder={t('webnotes-new_page')}
            view={UiInputView.LABEL_TOP}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <div className="font-normal uppercase text-[10px] text-neutral-500 leading-[120%] mb-2">
            {t('webnotes-page_status')}
          </div>
          <div className="text-neutral-800 text-[12px]">
            <Checkbox
              id="visible-in-portal"
              type={CheckboxType.SWITCH}
              checked={noteVisibility}
              onChange={handleVisibilityChange}
              label={noteVisibility ? t('webnotes-visible') : t('webnotes-invisible')}
              boxSize={CheckboxSize.LG}
            />
          </div>
        </div>
        <div className="p-4 mt-2 rounded border border-neutral-200 bg-neutral-50 border-solid leading-[120%] tracking-[0.2px] text-[12px]">
          <span className="not-italic font-normal text-neutral-800">
            <b>{t('webnotes-add_to_portal_description_title')}</b> <br />
            {t('webnotes-add_to_portal_description_text_1')} <br />
            {t('webnotes-add_to_portal_description_text_2')}
          </span>
        </div>
        <div className="w-full flex justify-end gap-[12px]">
          <Button type={ButtonType.STROKED} size={ButtonSize.MD} onClick={closeModal}>
            {t('webnotes-close')}
          </Button>
          <Button type={ButtonType.FLAT} size={ButtonSize.MD} onClick={handleAddPage}>
            {t('webnotes-add_page')}
          </Button>
        </div>
      </div>
    </div>
  );
}
